import { useState, type FormEvent } from 'react';
import { Loader2, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog.js';
import { Button } from '@/components/ui/Button.js';
import { Input } from '@/components/ui/Input.js';
import { Label } from '@/components/ui/Label.js';
import { api, ApiError, type InviteMemberResponse } from '@/api/client.js';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  orgId: string;
  orgName: string;
  onInvited: () => void;
}

// Friendly labels for the permission picker. Source of truth is the API's
// permissions registry — when that endpoint lands, this list comes from there.
const ROLE_OPTIONS: Array<{ value: string[]; label: string; description: string }> = [
  {
    value: ['core.org.read'],
    label: 'Viewer',
    description: 'Can read org metadata, see members and applications.',
  },
  {
    value: ['core.org.admin'],
    label: 'Administrator',
    description: 'Full admin: invite members, create child orgs, configure SSO.',
  },
];

export function InviteMemberDialog({ open, onOpenChange, orgId, orgName, onInvited }: Props): JSX.Element {
  const [stage, setStage] = useState<'form' | 'invite'>('form');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<number>(0);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<InviteMemberResponse | null>(null);
  const [copied, setCopied] = useState(false);

  function reset() {
    setStage('form');
    setEmail('');
    setRole(0);
    setError(null);
    setSubmitting(false);
    setResponse(null);
    setCopied(false);
  }

  async function submit(e: FormEvent) {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const res = await api.organisations.inviteMember(orgId, {
        email,
        grantedPermissions: ROLE_OPTIONS[role]!.value,
      });
      setResponse(res);
      setStage('invite');
      onInvited();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not send invitation.');
    } finally {
      setSubmitting(false);
    }
  }

  async function copyLink() {
    if (!response) return;
    await navigator.clipboard.writeText(response.acceptUrl);
    setCopied(true);
    toast.success('Invite link copied');
    setTimeout(() => setCopied(false), 2_000);
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <DialogContent>
        {stage === 'form' ? (
          <>
            <DialogHeader>
              <DialogTitle>Invite a member</DialogTitle>
              <DialogDescription>
                They'll receive a one-shot link. On accept they pick a name + password and join{' '}
                <span className="font-medium text-foreground">{orgName}</span> with the chosen role.
              </DialogDescription>
            </DialogHeader>
            <form className="space-y-4" onSubmit={submit}>
              <div className="space-y-1.5">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  autoFocus
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="alice@example.com"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Role</Label>
                <div className="space-y-2">
                  {ROLE_OPTIONS.map((opt, i) => (
                    <label
                      key={opt.label}
                      className="flex cursor-pointer items-start gap-3 rounded-md border border-border bg-background p-3 hover:bg-accent/40"
                    >
                      <input
                        type="radio"
                        name="role"
                        checked={role === i}
                        onChange={() => setRole(i)}
                        className="mt-0.5"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium">{opt.label}</div>
                        <div className="text-xs text-muted-foreground">{opt.description}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
              {error && (
                <p className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                  {error}
                </p>
              )}
              <DialogFooter>
                <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting}>
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Send invitation'}
                </Button>
              </DialogFooter>
            </form>
          </>
        ) : response ? (
          <>
            <DialogHeader>
              <DialogTitle>Invitation ready</DialogTitle>
              <DialogDescription>
                Share this one-shot link with{' '}
                <span className="font-medium text-foreground">{response.email}</span>. Expires{' '}
                {new Date(response.expiresAt).toLocaleDateString()}.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="rounded-md border border-border bg-background px-3 py-2 font-mono text-xs break-all">
                {response.acceptUrl}
              </div>
              <Button onClick={copyLink} variant="secondary" className="w-full">
                {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                {copied ? 'Copied' : 'Copy link'}
              </Button>
              <p className="text-xs text-muted-foreground">
                DeployIt doesn't email this yet. Paste it wherever {response.email} will see it.
              </p>
            </div>
            <DialogFooter>
              <Button
                onClick={() => {
                  onOpenChange(false);
                }}
              >
                Done
              </Button>
            </DialogFooter>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
